import { useState, useEffect, useCallback, useRef } from "react";
import {
  getOkrs,
  getOkrsDigest,
  createObjective,
  updateObjective,
  createKeyResult,
  updateKeyResult,
  createInitiative,
  updateInitiative,
  putInitiativeUpdate
} from "../../data/okrsClient.js";

export function useOkrs(initialLens = "overall") {
  const [lens, setLens] = useState(initialLens);
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [digestData, setDigestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestId = useRef(0);

  const loadData = useCallback(async (currentLens, currentYear, currentMonth) => {
    const request = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      if (currentLens === "digest") {
        const res = await getOkrsDigest(currentYear, currentMonth);
        if (request === requestId.current) setDigestData(res);
      } else {
        const res = await getOkrs(currentLens, currentYear);
        if (request === requestId.current) setData(res);
      }
    } catch (err) {
      if (request === requestId.current) setError(err);
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(lens, year, month);
  }, [lens, year, month, loadData]);

  const handleLensChange = (newLens) => setLens(newLens);
  const handleYearChange = (newYear) => {
    setYear(newYear);
    setMonth((previous) => `${newYear}-${previous.slice(5)}`);
  };
  const handleMonthChange = (newMonth) => setMonth(newMonth);

  const mutate = useCallback(async (action, ...args) => {
    try {
      const res = await action(...args);
      await loadData(lens, year, month);
      return res;
    } catch (err) {
      console.error("Mutation failed", err);
      throw err;
    }
  }, [lens, year, month, loadData]);

  return {
    lens, handleLensChange,
    year, handleYearChange,
    month, handleMonthChange,
    data: lens === "digest" ? (digestData?.year === year && digestData?.month === month ? digestData : null) : (data?.year === year && data?.lens === lens ? data : null),
    loading, error,
    refresh: () => loadData(lens, year, month),
    addObjective: (payload) => mutate(createObjective, payload),
    editObjective: (id, patch) => mutate(updateObjective, id, patch),
    addKeyResult: (payload) => mutate(createKeyResult, payload),
    editKeyResult: (id, patch) => mutate(updateKeyResult, id, patch),
    addInitiative: (payload) => mutate(createInitiative, payload),
    editInitiative: (id, patch) => mutate(updateInitiative, id, patch),
    saveInitiativeUpdate: (id, updateMonth, payload) => mutate(putInitiativeUpdate, id, updateMonth, payload),
  };
}
